import * as React from 'react'

import type { ModelSuggestion } from '#/shared/api/generated/shipment/models'
import { Combobox } from '#/shared/ui/combobox'
import type { ComboboxOption } from '#/shared/ui/combobox'

import { useAutoSelectExact } from '../model/use-auto-select-exact'
import { useComboboxFreeze } from '../model/use-combobox-freeze'
import { useModelSearch } from '../model/use-model-search'

export interface ModelSelection {
  model: string
  /** Canonical vehicle type guessed for the model, `null` when the catalog has none. */
  type?: ModelSuggestion['type']
  /** 0–1 share of VIN patterns backing `type`; drives how assertively we auto-fill it. */
  typeConfidence?: ModelSuggestion['type_confidence']
}

interface ModelComboboxProps {
  value: string
  onValueChange: (value: string) => void
  onSelect?: (selection: ModelSelection) => void
  make?: string
  label?: string
  placeholder?: string
  error?: boolean
  disabled?: boolean
  className?: string
}

const ALWAYS_PASS_FILTER = () => true

export function ModelCombobox({
  value,
  onValueChange,
  onSelect,
  make,
  label,
  placeholder,
  error,
  disabled,
  className,
}: ModelComboboxProps) {
  const freeze = useComboboxFreeze<ModelSuggestion>(value)
  const [open, setOpen] = React.useState(false)
  const live = useModelSearch({
    make,
    query: value,
    enabled: (open || freeze.isUserTyped) && !freeze.isJustSelected,
  })
  const items = freeze.pickItems(live.items)
  const options: ComboboxOption[] = items.map((it) => ({
    value: it.model,
    label: it.model,
    tag: live.hasMakeFilter ? undefined : it.make,
  }))

  const handleValueChange = (next: string) => {
    freeze.onTyped(next)
    onValueChange(next)
  }

  const handleSelect = (option: ComboboxOption) => {
    // Re-picking the value that's already committed is a no-op for downstream
    // fields (lot/vin/type stay put).
    const isReselect = option.label === value
    freeze.freeze(option.label, items)
    if (isReselect) return
    const picked = items.find((it) => it.model === option.value)
    onSelect?.({
      model: option.value,
      type: picked?.type,
      typeConfidence: picked?.type_confidence,
    })
  }

  useAutoSelectExact({
    enabled: freeze.isUserTyped && !freeze.isJustSelected,
    value,
    items,
    getLabel: (it) => it.model,
    onMatch: (it) => {
      freeze.freeze(it.model, items)
      onValueChange(it.model)
      onSelect?.({
        model: it.model,
        type: it.type,
        typeConfidence: it.type_confidence,
      })
    },
  })

  return (
    <Combobox
      label={label}
      placeholder={placeholder}
      options={options}
      value={value}
      onValueChange={handleValueChange}
      onSelect={handleSelect}
      onOpenChange={setOpen}
      filter={ALWAYS_PASS_FILTER}
      loading={live.isLoading}
      hideEmpty={live.needsMoreInput && !freeze.isUserTyped}
      hideToggle
      error={error}
      disabled={disabled}
      className={className}
      clearable
    />
  )
}
